import { FC, useMemo, useState } from 'react';
import {
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import useGetPartner from '../../hooks/useGetPartner';
import useGetPartnerBoats from '../../hooks/useGetPartnerBoats';
import useGetPartnerBookings from '../../hooks/useGetPartnerBookings';
import useGetPartnerTrips from '../../hooks/useGetPartnerTrips';
import { getErrorMessage } from '../../utils/errorUtils';
import {
  subDays,
  startOfDay,
  endOfDay,
  isWithinInterval,
  differenceInDays,
} from 'date-fns';
import StatsCard from './components/StatsCard';
import QuickActions from './components/QuickActions';
import RecentActivity from './components/RecentActivity';
import StatusBadge from './components/StatusBadge';
import {
  IconShip,
  IconClipboardList,
  IconCalendar,
  IconTrendingUp,
} from '@tabler/icons-react';

type DateRange = '7d' | '30d' | '90d' | 'all';

const PartnerHome: FC = () => {
  const { partner } = useGetPartner();
  const {
    boats,
    isLoading: boatsLoading,
    isError: boatsError,
    error: boatsErrorDetails,
  } = useGetPartnerBoats();
  const {
    bookings,
    isLoading: bookingsLoading,
    isError: bookingsError,
    error: bookingsErrorDetails,
  } = useGetPartnerBookings();
  const {
    trips,
    isLoading: tripsLoading,
    isError: tripsError,
    error: tripsErrorDetails,
  } = useGetPartnerTrips();

  const [dateRange, setDateRange] = useState<DateRange>('30d');

  const isLoading = boatsLoading || bookingsLoading || tripsLoading;
  const hasError = boatsError || bookingsError || tripsError;

  // Calculate date range filter
  const dateRangeFilter = useMemo(() => {
    const now = new Date();
    switch (dateRange) {
      case '7d':
        return { start: startOfDay(subDays(now, 7)), end: endOfDay(now) };
      case '30d':
        return { start: startOfDay(subDays(now, 30)), end: endOfDay(now) };
      case '90d':
        return { start: startOfDay(subDays(now, 90)), end: endOfDay(now) };
      case 'all':
      default:
        return null;
    }
  }, [dateRange]);

  // Filter bookings by date range
  const filteredBookings = useMemo(() => {
    if (!dateRangeFilter) return bookings;
    return bookings.filter(booking =>
      isWithinInterval(new Date(booking.embarkationDate), dateRangeFilter),
    );
  }, [bookings, dateRangeFilter]);

  // Calculate stats
  const stats = useMemo(() => {
    const confirmedBookings = filteredBookings.filter(
      b => b.bookingStatus === 1,
    );
    const totalBookings = filteredBookings.length;
    const conversionRate =
      totalBookings > 0
        ? Math.round((confirmedBookings.length / totalBookings) * 100)
        : 0;

    const upcomingBookings = bookings.filter(
      booking =>
        new Date(booking.embarkationDate) >= new Date() &&
        booking.bookingStatus === 1,
    );

    const upcomingTrips = trips.filter(
      trip => new Date(trip.embarkationDate) >= new Date(),
    );

    const activeBoats = boats.length;

    // Calculate average guest count per booking (as a proxy for average booking value)
    const avgGuestCount =
      confirmedBookings.length > 0
        ? Math.round(
            confirmedBookings.reduce((sum, b) => sum + b.guestCount, 0) /
              confirmedBookings.length,
          )
        : 0;

    // Calculate previous period for comparison (for trends)
    let previousPeriodBookings: typeof bookings = [];
    if (dateRangeFilter) {
      const periodDuration = differenceInDays(
        dateRangeFilter.end,
        dateRangeFilter.start,
      );
      const previousPeriodStart = subDays(
        dateRangeFilter.start,
        periodDuration,
      );
      const previousPeriodEnd = dateRangeFilter.start;

      previousPeriodBookings = bookings.filter(booking =>
        isWithinInterval(new Date(booking.embarkationDate), {
          start: previousPeriodStart,
          end: previousPeriodEnd,
        }),
      );
    }

    const previousConfirmed = previousPeriodBookings.filter(
      b => b.bookingStatus === 1,
    ).length;
    const currentConfirmed = confirmedBookings.length;
    const bookingTrend =
      previousConfirmed > 0
        ? Math.round(
            ((currentConfirmed - previousConfirmed) / previousConfirmed) * 100,
          )
        : currentConfirmed > 0
          ? 100
          : 0;

    return {
      totalBookings: filteredBookings.length,
      confirmedBookings: confirmedBookings.length,
      conversionRate,
      upcomingBookings: upcomingBookings.length,
      upcomingTrips: upcomingTrips.length,
      activeBoats,
      avgGuestCount,
      bookingTrend,
    };
  }, [filteredBookings, bookings, trips, boats, dateRangeFilter]);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (hasError) {
    const errorMessage =
      getErrorMessage(boatsErrorDetails) ||
      getErrorMessage(bookingsErrorDetails) ||
      getErrorMessage(tripsErrorDetails) ||
      'Unknown error';

    return (
      <div className="mx-auto max-w-7xl">
        <Alert severity="error" className="mb-4">
          <div className="font-semibold">Error loading data</div>
          <div className="mt-2 text-sm">{errorMessage}</div>
          <div className="mt-2 text-xs text-gray-600">
            Please try refreshing the page or contact support if the issue
            persists.
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      {/* Header with Status */}
      <div className="mb-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Welcome back, {partner?.name || 'Partner'}
            </h1>
            <p className="mt-2 text-gray-600">
              Here's an overview of your partner account
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {partner?.status && (
              <StatusBadge type="status" value={partner.status} />
            )}
            {partner?.kycState && (
              <StatusBadge type="kyc" value={partner.kycState} />
            )}
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="mb-6 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Filter booking stats by date range
        </div>
        <FormControl size="small" className="min-w-[150px]">
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateRange}
            label="Date Range"
            onChange={e => setDateRange(e.target.value as DateRange)}
          >
            <MenuItem value="7d">Last 7 days</MenuItem>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
            <MenuItem value="all">All time</MenuItem>
          </Select>
        </FormControl>
      </div>

      {/* Stats Cards */}
      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total Bookings"
          value={stats.totalBookings}
          trend={
            stats.bookingTrend !== 0
              ? {
                  value: stats.bookingTrend,
                  label: 'vs previous period',
                  isPositive: stats.bookingTrend > 0,
                }
              : undefined
          }
          icon={<IconClipboardList className="h-6 w-6" />}
          linkTo="/partner/bookings"
          subtitle={`${stats.confirmedBookings} confirmed`}
        />
        <StatsCard
          title="Conversion Rate"
          value={`${stats.conversionRate}%`}
          icon={<IconTrendingUp className="h-6 w-6" />}
          subtitle={
            stats.totalBookings > 0
              ? `${stats.confirmedBookings} of ${stats.totalBookings} confirmed`
              : 'No bookings yet'
          }
        />
        <StatsCard
          title="Upcoming Trips"
          value={stats.upcomingTrips}
          icon={<IconCalendar className="h-6 w-6" />}
          linkTo="/partner/trips"
          subtitle="Scheduled trips"
        />
        <StatsCard
          title="Active Boats"
          value={stats.activeBoats}
          icon={<IconShip className="h-6 w-6" />}
          linkTo="/partner/boats"
          subtitle="Total boats"
        />
      </div>

      {/* Additional Stats Row */}
      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2">
        <StatsCard
          title="Upcoming Bookings"
          value={stats.upcomingBookings}
          icon={<IconClipboardList className="h-6 w-6" />}
          linkTo="/partner/bookings"
          subtitle="Confirmed bookings"
        />
        <StatsCard
          title="Avg Guests per Booking"
          value={stats.avgGuestCount}
          subtitle={
            stats.confirmedBookings > 0
              ? `Based on ${stats.confirmedBookings} confirmed bookings`
              : 'No confirmed bookings'
          }
        />
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <QuickActions />
      </div>

      {/* Recent Activity */}
      <div className="mb-8">
        <RecentActivity bookings={bookings} trips={trips} maxItems={10} />
      </div>

      {/* Empty State for No Data */}
      {bookings.length === 0 && trips.length === 0 && boats.length === 0 && (
        <div className="rounded-lg bg-white p-12 text-center shadow-sm">
          <div className="mx-auto max-w-md">
            <IconShip className="mx-auto h-16 w-16 text-gray-400" />
            <h3 className="mt-4 text-lg font-semibold text-gray-900">
              Welcome to your Partner Portal
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Get started by adding your first boat or checking your bookings.
              Your dashboard will populate as you add content.
            </p>
            <div className="mt-6">
              <QuickActions />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerHome;
